import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { searchRequestOptions, type RequestOption } from '@/lib/services/caseThreadsService'
import { SearchableOptions } from './SearchableOptions'

type Props = {
  isOpen: boolean
  suggestedId?: number | null
  onClose: () => void
  onAttach: (ticketRequestId: number) => Promise<void>
}

function requestLabel(request: RequestOption): string {
  const title = request.subject?.trim() || 'Ticket request'
  return `${request.request_number} — ${title}`
}

export function AttachToRequestModal({ isOpen, suggestedId, onClose, onAttach }: Props) {
  const [query, setQuery] = useState('')
  const [requests, setRequests] = useState<RequestOption[]>([])
  const [requestId, setRequestId] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    setQuery('')
    setRequestId(null)
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    let cancelled = false
    const handle = window.setTimeout(() => {
      setLoading(true)
      searchRequestOptions(query, suggestedId)
        .then((rows) => {
          if (!cancelled) setRequests(rows)
        })
        .catch(() => {
          if (!cancelled) setRequests([])
        })
        .finally(() => {
          if (!cancelled) setLoading(false)
        })
    }, 250)
    return () => {
      cancelled = true
      window.clearTimeout(handle)
    }
  }, [isOpen, query, suggestedId])

  const submit = async () => {
    if (!requestId) return
    setSaving(true)
    try {
      await onAttach(requestId)
      onClose()
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Attach to existing Ticket Request</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <SearchableOptions
            label="Ticket request"
            placeholder="Search by request number, subject, or contact"
            query={query}
            selectedValue={requestId}
            loading={loading}
            emptyText="No ticket requests match that search"
            options={requests.map((request) => ({
              value: request.id,
              label: requestLabel(request),
              hint: request.contact_name || request.contact_email || undefined,
            }))}
            onQueryChange={(next) => {
              setQuery(next)
              setRequestId(null)
            }}
            onSelect={(value) => setRequestId(value)}
          />
        </ModalBody>
        <ModalFooter>
          <Button mr={2} onClick={onClose} variant="ghost">
            Cancel
          </Button>
          <Button colorScheme="blue" isDisabled={!requestId} isLoading={saving} onClick={() => void submit()}>
            Attach
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
